import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import useWindowSize from '../../hooks/useWindowSize';
import styles from './full-screen-media.module.scss';
import MediaProps from '../../models/media-component';
import BreakPoints from '../../constants/break-points';
import DOMIDs from '../../constants/DOM-IDs';
import isInViewport from '../../helpers/is-in-viewport';
import { generateBlurDataURL } from '../../helpers/image-placeholders';

type Side = 'right' | 'left' | null;

type Props = {
  data: MediaProps;
  anchorSide?: Side;
};

type CustomStyleProps = {
  anchorSide?: Side;
};

const CustomStyles = styled.div`
  ${(customStyleProps: CustomStyleProps) => {
    const { anchorSide } = customStyleProps;

    return anchorSide
      ? `.${styles['full-screen-media__video']} { object-position: top ${anchorSide}; }`
      : '';
  }}
`;

const FullScreenMedia = ({ data, anchorSide }: Props) => {
  const { width } = useWindowSize();
  const imageRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLInputElement>(null);
  const parallaxClass = styles['full-screen-media__parallax'];
  const media =
    width < BreakPoints.md && data.mobile ? data.mobile : data.desktop;

  if (!media) return <></>;

  const display = (ref: React.RefObject<HTMLInputElement>) => {
    if (
      !ref ||
      !ref.current ||
      !wrapperRef ||
      !wrapperRef.current ||
      !mediaRef ||
      !mediaRef.current
    )
      return;

    const { top, bottom } = mediaRef.current.getBoundingClientRect();
    const portalParallaxMedia = document.getElementById(
      DOMIDs.portalMediaParallax
    );

    // Set opacity
    if (bottom <= window.innerHeight && bottom >= 0) {
      let opacity = bottom / window.innerHeight;
      opacity = opacity < 0 ? 0 : opacity;
      opacity = opacity > 1 ? 1 : opacity;

      // eslint-disable-next-line no-param-reassign
      ref.current.style.opacity = opacity.toString();
    }

    // Add parallax animation to portal
    if (portalParallaxMedia) {
      portalParallaxMedia.classList.add(styles['full-screen-media__wrapper']);
    }

    const element = portalParallaxMedia || wrapperRef.current;

    if (top <= 0) {
      // Add parallax class
      if (!element.classList.contains(parallaxClass))
        element.classList.add(parallaxClass);

      if (portalParallaxMedia) portalParallaxMedia.appendChild(ref.current);
    } else {
      // Remove parallax class
      element.classList.remove(parallaxClass);

      if (portalParallaxMedia) wrapperRef.current.appendChild(ref.current);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const ref = videoRef.current ? videoRef : imageRef;

      if (!data || !data.useParallaxEffect || !ref || !ref.current) return;

      if (isInViewport(mediaRef)) {
        // eslint-disable-next-line no-param-reassign
        ref.current.style.display = 'block';
        display(videoRef.current ? videoRef : imageRef);
      } else {
        ref.current.style.display = 'none';
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={mediaRef} className={styles['full-screen-media']}>
      <div ref={wrapperRef} className={styles['full-screen-media__wrapper']}>
        {media.isVideo() ? (
          <div ref={videoRef}>
            <CustomStyles anchorSide={anchorSide}>
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={data.poster?.src}
                aria-hidden="true"
                className={styles['full-screen-media__video']}
              >
                <source src={media.src} type="video/mp4" />
              </video>
            </CustomStyles>
          </div>
        ) : (
          <div ref={imageRef}>
            <Image
              placeholder="blur"
              alt={media.alt}
              src={media.src}
              layout="fill"
              objectFit="cover"
              quality={100}
              className={styles['full-screen-media__image']}
              blurDataURL={generateBlurDataURL(media.src)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FullScreenMedia;
